package com.bikompanion.ui.composables

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ErrorOutline
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import dev.chrisbanes.haze.HazeInput
import dev.chrisbanes.haze.HazeState
import dev.chrisbanes.haze.glass.hazeGlass
import dev.chrisbanes.haze.hazeSource
import kotlinx.coroutines.delay
import com.bikompanion.ui.preview.GlassPreviewSurface
import kotlin.time.Duration.Companion.milliseconds

/** Android's `Toast.LENGTH_LONG`. */
private val ToastDuration = 3500.milliseconds

private val ToastMaxWidth = 480.dp
private val ToastShape = RoundedCornerShape(24.dp)
private val ToastElevation = 6.dp

// Enough to hold the text against a busy map without cancelling the refraction under it.
private const val ToastPlateAlpha = 0.6f

/**
 * A glass error toast, behaving like Android's own: it takes no input, fades in when [show] turns
 * true, and fades out by itself after [ToastDuration] even if [show] stays true.
 *
 * Must be a sibling of [hazeState]'s source content, for the reason given on `HomeNavigationBar`.
 */
@Composable
internal fun GlassToast(
    show: Boolean,
    message: String,
    hazeState: HazeState,
    modifier: Modifier = Modifier,
) {
    var visible by remember { mutableStateOf(false) }
    LaunchedEffect(show) {
        visible = show
        if (show) {
            delay(ToastDuration)
            visible = false
        }
    }
    AnimatedVisibility(
        visible = visible,
        enter = fadeIn(),
        exit = fadeOut(),
        modifier = modifier,
    ) {
        GlassToastContent(message = message, hazeState = hazeState)
    }
}

@Composable
private fun GlassToastContent(
    message: String,
    hazeState: HazeState,
) {
    GlassToastSurface(hazeState = hazeState) {
        Icon(
            imageVector = Icons.Default.ErrorOutline,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.error,
        )
        Text(
            text = message,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurface,
        )
    }
}

/**
 * The glass plate every toast sits on.
 *
 * @param hazeSourceZIndex set for a toast that a sheet can open over, so the sheet's glass shows it
 *   instead of hiding it. The order is the check-bikes button's: the clip has to stay inside the
 *   source, or a sheet samples a rectangle.
 */
@Composable
internal fun GlassToastSurface(
    hazeState: HazeState,
    modifier: Modifier = Modifier,
    hazeSourceZIndex: Float? = null,
    content: @Composable RowScope.() -> Unit,
) {
    val glassTint = MaterialTheme.colorScheme.primaryContainer
    val glassStyle = remember(glassTint) { defaultGlassStyle(glassTint, ToastShape) }
    val shadowColor = MaterialTheme.colorScheme.scrim
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        modifier = modifier
            .widthIn(max = ToastMaxWidth)
            .shadow(
                elevation = ToastElevation,
                shape = ToastShape,
                clip = false,
                ambientColor = shadowColor,
                spotColor = shadowColor,
            ).then(
                if (hazeSourceZIndex != null) {
                    Modifier.hazeSource(hazeState, zIndex = hazeSourceZIndex)
                } else {
                    Modifier
                },
            ).clip(ToastShape)
            .hazeGlass(
                input = HazeInput.Sources(hazeState),
                style = glassStyle,
            ).background(MaterialTheme.colorScheme.surfaceContainer.copy(alpha = glassPlateAlpha(ToastPlateAlpha)))
            .padding(horizontal = 16.dp, vertical = 12.dp),
        content = content,
    )
}

// The content rather than [GlassToast], which only shows after an effect has run and a fade played.
@Preview
@Composable
private fun GlassToastPreview() {
    GlassPreviewSurface { hazeState ->
        GlassToastContent(
            message = "Couldn't load bike networks. Check your connection.",
            hazeState = hazeState,
        )
    }
}
